import { inject, Injectable, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { EMPTY, distinctUntilChanged, filter, map, merge, of, take, tap } from 'rxjs';

import { DomainQuery } from '@common/store/domain.store';
import { stripDialogLinkDeep } from '@common/util/dialog-link-params';

@Injectable({
  providedIn: 'root',
})
export class GtmService {
  constructor(private domainQuery: DomainQuery) {}

  private document = inject(DOCUMENT);
  private router = inject(Router);

  initGTM() {
    this.domainQuery.domain$
      .pipe(
        filter((domain) => !!domain?.config?.gtm?.tag_id),
        take(1),
        tap(() => {
          const gtmTagId = this.domainQuery.getValue().config.gtm?.tag_id;

          if (!gtmTagId) {
            console.warn('GTM tag ID is not set, skipping GTM initialization');
            return;
          }

          (function (w: Window & typeof globalThis, d: Document, s: string, l: string, i: string): void {
            // eslint-disable-next-line
            (w as any)[l] = (w as any)[l] || [];
            // eslint-disable-next-line
            (w as any)[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

            const f = d.getElementsByTagName(s)[0];
            // eslint-disable-next-line
            const j = d.createElement(s) as any;
            j.async = true;
            j.src = `https://www.googletagmanager.com/gtm.js?id=${i}&l=${l}`;
            f.parentNode?.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', gtmTagId);

          // Step 3: Add <noscript> fallback to <body>
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.injectNoScript(gtmTagId));
          } else {
            this.injectNoScript(gtmTagId);
          }

          this.trackPageViews();

          console.debug('GTM initialized');
        }),
      )
      .subscribe();
  }

  /**
   * Pushes one `aiwb_page_view` per page (the landing view, then each change of the URL without its dialog
   * link). `dlg`/`entity` never reach the dataLayer, and opening or closing a dialog is not a page view.
   * Root singleton and set up once (`take(1)` upstream), so the subscription lives for the app.
   */
  trackPageViews(): void {
    merge(
      // Before the first navigation ends router.url is still '/': that view comes with the first NavigationEnd.
      this.router.navigated ? of(this.router.url) : EMPTY,
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.urlAfterRedirects),
      ),
    )
      .pipe(
        map((u) => stripDialogLinkDeep(u)),
        distinctUntilChanged(),
      )
      .subscribe((path) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({
          event: 'aiwb_page_view',
          page_location: window.location.origin + path,
          page_path: path,
          page_title: this.document.title,
        });
      });
  }

  private injectNoScript(gtmId: string): void {
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');

    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    noscript.appendChild(iframe);
    document.body.appendChild(noscript);
  }
}
